import { TFunction } from '@psyomics/ui-common';
import { Button, ColumnDef } from '@psyomics/ui-components';
import { Pencil, Send } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { RespondentType } from '../generated/home';

import { formatRespondentName } from './respondent-utils';
import { DraftReferralRequestResult } from './types';

function patientNameColumn(t: TFunction): ColumnDef<DraftReferralRequestResult> {
  return {
    accessorFn: (row) =>
      formatRespondentName(
        row.respondents.find((respondent) => respondent.respondentType === RespondentType.Patient),
        t
      ),
    cell: (info) => info.getValue(),
    enableSorting: false,
    header: t('page.professionalReferral.home.table.patientName'),
    id: 'patientName',
  };
}

function piSystemIdColumn(t: TFunction): ColumnDef<DraftReferralRequestResult> {
  return {
    accessorFn: (row) => row.piSystemId ?? t('page.professionalReferral.home.table.notProvided'),
    cell: (info) => info.getValue(),
    enableSorting: false,
    header: t('page.professionalReferral.home.table.piSystemId'),
    id: 'piSystemId',
  };
}

function organisationColumn(t: TFunction): ColumnDef<DraftReferralRequestResult> {
  return {
    accessorKey: 'organisationName',
    cell: (info) => info.getValue(),
    enableSorting: false,
    header: t('page.professionalReferral.home.table.organisation'),
  };
}

function serviceColumn(t: TFunction): ColumnDef<DraftReferralRequestResult> {
  return {
    accessorKey: 'serviceDescription',
    cell: (info) => info.getValue(),
    enableSorting: false,
    header: t('page.professionalReferral.home.table.service'),
  };
}

function actionsColumn(t: TFunction): ColumnDef<DraftReferralRequestResult> {
  return {
    id: 'actions',
    header: '',
    cell: (info) => {
      const { id } = info.row.original;

      return (
        <div className="flex gap-2">
          <Link to={`/referral-request/${id}/edit`}>
            <Button appearance="secondary" aria-label={t('common.buttons.edit')} icon={Pencil} size="sm" />
          </Link>
          <Link to={`/referral-request/${id}/submit`}>
            <Button appearance="secondary" aria-label={t('common.buttons.send')} icon={Send} size="sm" />
          </Link>
        </div>
      );
    },
  };
}

const useTableColumns = (): ColumnDef<DraftReferralRequestResult>[] => {
  const { t } = useTranslation();

  return [patientNameColumn(t), piSystemIdColumn(t), organisationColumn(t), serviceColumn(t), actionsColumn(t)];
};

export { useTableColumns };
